/**
 * VaultFilePickerDialog — shared picker for selecting Vault Documents.
 *
 * Replaces the legacy `@/components/files/FileBrowser` + `FileUpload`
 * pickers (which talked to `user_files`). One dialog, used by every
 * arche-specific "pick a file" surface (chat AI-context, Travel
 * attachments, Social Media media picker, Image Editor file picker,
 * Icon dev-console picker, image-generator reference image).
 *
 * Surfaces a list of Vault documents from the user's Personal Holder,
 * optionally filtered by MIME family; supports upload-new via the
 * Vault upload-init / presigned PUT / finalize flow. Returns a
 * `VaultPickerResult` with both the durable `documentId` (re-resolvable
 * later) and a fresh 1-hour `downloadUrl` (for immediate display).
 *
 * Single-select is the default; pass `multiSelect` for galleries.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  File as FileIcon,
  Image as ImageIcon,
  Loader2,
  Music,
  Search,
  Upload,
  X,
  Folder,
  ChevronRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { api, type VaultDocumentDto, type VaultDocumentFolderDto } from '@/lib/api';
import { uploadLocalFileToVault } from '@/lib/vault-upload';
import { captureError } from '@/lib/telemetry';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface VaultPickerResult {
  /** Vault document id — the durable handle. */
  documentId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  /** Vault s3Key — usable as a stable ref for downstream API calls. */
  s3Key: string;
  /** Fresh 1-hour signed download URL for `<img src>`, `<audio src>`, fetch(). */
  downloadUrl: string;
}

/** Built-in MIME family filters. */
export type VaultPickerMimeFamily = 'image' | 'audio' | 'video' | 'document';

export interface VaultFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Single-select callback. Fires when the user confirms a selection. */
  onFileSelected?: (file: VaultPickerResult) => void;
  /** Multi-select callback. Required when `multiSelect` is true. */
  onFilesSelected?: (files: VaultPickerResult[]) => void;

  /** Default: false. When true, the dialog renders a checkbox column. */
  multiSelect?: boolean;

  /** Dialog title — defaults to "Select File". */
  title?: string;
  /** Short description shown below the title. */
  description?: string;
  /** Confirm-button label. Defaults to "Select" / "Add N files". */
  confirmLabel?: string;

  /** Filter listed docs by a MIME family or a custom predicate. */
  mimeFilter?: VaultPickerMimeFamily | ((doc: VaultDocumentDto) => boolean);

  /** Show / hide the "Upload new" button. Default: true. */
  allowUpload?: boolean;
  /** Restrict the upload `<input>`'s accept attribute. */
  uploadAccept?: string;
  /**
   * Let the Upload button take several files at once. Default: false.
   *
   * Deliberately independent of `multiSelect` — picking many existing files and
   * uploading many new ones are separate capabilities, and a consumer that only
   * asked for the former should not silently acquire the latter.
   */
  multiUpload?: boolean;
  /** Per-file cap on uploads (bytes). Default: 50 MB. */
  maxUploadSize?: number;
  /** Optional pre-upload validator. Return a string to reject, null to allow. */
  validateUpload?: (file: File) => string | null;
  /**
   * Optional cap on how many entries can be in the selection set. `0` disables
   * selection entirely (the consumer is already at its own limit).
   */
  selectionLimit?: number;
  /**
   * Shown when the user tries to exceed `selectionLimit`. Defaults to a generic
   * sentence; pass a consumer-specific one ("up to 20 files per message") so the
   * user learns *why* rather than just *that*.
   */
  selectionLimitMessage?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const DEFAULT_MAX_UPLOAD = 50 * 1024 * 1024;

/**
 * Fallback copy for a blocked selection. A limit of 0 means the consumer is
 * already full, which "select at most 0 files" states in the least useful way
 * possible — say what the user should do instead.
 */
function defaultSelectionLimitMessage(limit: number): string {
  if (limit <= 0) return "You've already selected as many files as this allows.";
  return `You can select at most ${limit} file${limit === 1 ? '' : 's'}.`;
}

function mimeFamilyOf(mime: string | null | undefined): VaultPickerMimeFamily | 'other' {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (
    mime.startsWith('text/') ||
    mime === 'application/pdf' ||
    mime === 'application/json' ||
    mime.includes('officedocument') ||
    mime === 'application/msword'
  ) {
    return 'document';
  }
  return 'other';
}

function iconFor(mime: string | null | undefined) {
  switch (mimeFamilyOf(mime)) {
    case 'image':
      return ImageIcon;
    case 'audio':
      return Music;
    default:
      return FileIcon;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function passesFilter(
  doc: VaultDocumentDto,
  filter: VaultFilePickerDialogProps['mimeFilter'],
): boolean {
  if (!filter) return true;
  if (typeof filter === 'function') return filter(doc);
  return mimeFamilyOf(doc.mimeType) === filter;
}

async function toPickerResult(doc: VaultDocumentDto): Promise<VaultPickerResult> {
  const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
  return {
    documentId: doc.documentId,
    fileName: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
    s3Key: doc.s3Key,
    downloadUrl: url,
  };
}

// ---------------------------------------------------------------------------
// Panel — non-dialog version. Use directly when embedding the picker
// inside a multi-tab modal (see social-media MediaPickerModal).
// ---------------------------------------------------------------------------

export interface VaultFilePickerPanelProps {
  isActive: boolean;
  onFileSelected?: (file: VaultPickerResult) => void;
  onFilesSelected?: (files: VaultPickerResult[]) => void;
  multiSelect?: boolean;
  confirmLabel?: string;
  mimeFilter?: VaultFilePickerDialogProps['mimeFilter'];
  allowUpload?: boolean;
  uploadAccept?: string;
  multiUpload?: boolean;
  maxUploadSize?: number;
  validateUpload?: VaultFilePickerDialogProps['validateUpload'];
  /** Optional cap on how many entries can be in the selection set. */
  selectionLimit?: number;
  /** Message shown when `selectionLimit` would be exceeded. */
  selectionLimitMessage?: string;
  /** Optional className for the outer container. */
  className?: string;
}

export function VaultFilePickerPanel({
  isActive,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  multiUpload = false,
  maxUploadSize = DEFAULT_MAX_UPLOAD,
  validateUpload,
  selectionLimit,
  selectionLimitMessage,
  className,
}: VaultFilePickerPanelProps) {
  const [docs, setDocs] = useState<VaultDocumentDto[]>([]);
  const [folders, setFolders] = useState<VaultDocumentFolderDto[]>([]);
  /** Breadcrumb path; the last entry is the current folder. Root is
   *  `{ id: null }`. `listVaultDocumentFolders` returns children only,
   *  so we push/pop as the user drills in/out — no ancestry fetch is
   *  needed because navigation always begins at root. */
  const [folderStack, setFolderStack] = useState<Array<{ id: string | null; name: string }>>([
    { id: null, name: 'Home' },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Bumped by the error-state Retry button to re-trigger the load effect. */
  const [reloadKey, setReloadKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Map<string, VaultDocumentDto>>(new Map());
  const [holderId, setHolderId] = useState<string | null>(null);
  /** documentId → 1-hour signed URL for image thumbnails. Populated
   *  lazily after the list loads (one presign per image-mime row)
   *  rather than eagerly during list response — the list endpoint
   *  doesn't carry signed URLs and minting them on the read path
   *  would amplify cost. Pending rows fall back to the generic icon
   *  until their URL resolves. */
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());

  const currentFolderId = folderStack[folderStack.length - 1].id;
  // Boolean (not the raw query) so typing more characters re-filters
  // client-side without refetching on every keystroke; the fetch only
  // re-runs when search toggles on or off.
  const isSearchActive = search.trim().length > 0;

  // Resolve the Personal Holder and reset navigation when the panel
  // becomes active. Holder resolution is separate from the per-folder
  // load below so navigating folders doesn't re-fetch memberships.
  useEffect(() => {
    if (!isActive) return;
    setSelected(new Map());
    setSearch('');
    setThumbnails(new Map());
    setFolderStack([{ id: null, name: 'Home' }]);
    setLoadError(null);
    let cancelled = false;
    void api
      .listMyVaultMemberships()
      .then((memberships) => {
        if (cancelled) return;
        if (!memberships.personalHolder) {
          toast({
            title: 'Workspace not provisioned',
            description: 'Your personal workspace is missing.',
            variant: 'destructive',
          });
          return;
        }
        setHolderId(memberships.personalHolder.holderId);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isActive]);

  // Load the current view. Two modes:
  //  - browsing: the current folder's child folders + its files.
  //  - searching: cross-folder file results (folders hidden) so the
  //    long-standing global-search behavior existing callers rely on
  //    is preserved. Clearing the query returns to the current folder.
  // Re-runs on folder navigation and when search toggles on/off.
  //
  // Documents come from the cross-arche PICKER endpoint, which returns
  // ONLY `visibility = 'user-vault'` rows (arche-private docs stay hidden,
  // per docs/vault/arche-files-policy.md) in downloadable statuses. We do
  // NOT use the generic `listVaultDocuments` here — that filters by
  // sensitivity only and would leak arche-private rows into the picker.
  useEffect(() => {
    if (!isActive || !holderId) return;
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        if (isSearchActive) {
          // Cross-folder user-vault superset (no folderId); the name match is
          // applied client-side in `filtered` below, so the fetch doesn't read
          // the live query term (which isn't an effect dep).
          const result = await api.listVaultDocumentsForPicker(holderId as string, {
            limit: 500,
          });
          if (cancelled) return;
          setFolders([]);
          setDocs(result.documents);
        } else {
          const [folderRes, docRes] = await Promise.all([
            api.listVaultDocumentFolders(holderId as string, { parentFolderId: currentFolderId }),
            api.listVaultDocumentsForPicker(holderId as string, {
              folderId: currentFolderId,
              limit: 500,
            }),
          ]);
          if (cancelled) return;
          setFolders(folderRes.folders);
          setDocs(docRes.documents);
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [isActive, holderId, currentFolderId, isSearchActive, reloadKey]);

  // Single-select: don't carry a hidden selection across views. A file
  // picked in one folder isn't visible after navigating elsewhere, so
  // confirming it would be surprising. Multi-select galleries intentionally
  // accumulate across folders (the footer surfaces the running count).
  useEffect(() => {
    if (!multiSelect) setSelected(new Map());
  }, [currentFolderId, isSearchActive, multiSelect]);

  // Resolve presigned download URLs for image-mime docs so the list
  // can render real thumbnails instead of the generic ImageIcon. Runs
  // after `docs` populates; each URL lands in the map as it returns
  // so the user sees thumbnails fill in progressively rather than
  // waiting for all of them. `Promise.allSettled` keeps a single
  // failed presign from blocking the rest. Skipped when no image
  // docs are present so non-image pickers (Document Sign, etc.)
  // don't trigger any extra requests.
  useEffect(() => {
    if (!isActive || docs.length === 0) return;
    const imageDocs = docs.filter((d) => mimeFamilyOf(d.mimeType) === 'image');
    if (imageDocs.length === 0) return;
    let cancelled = false;
    void Promise.allSettled(
      imageDocs.map(async (doc) => {
        const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
        if (cancelled) return;
        setThumbnails((prev) => {
          if (prev.has(doc.documentId)) return prev;
          const next = new Map(prev);
          next.set(doc.documentId, url);
          return next;
        });
      }),
    );
    return () => {
      cancelled = true;
    };
  }, [docs, isActive]);

  const filtered = docs.filter((d) => {
    // `pending` = row created but bytes not yet PUT to S3. Trying to
    // download one would 404, so don't surface them. Every other
    // non-archived/non-deleted status has bytes in the bucket and is
    // a valid selection target.
    if (d.status === 'pending') return false;
    if (!passesFilter(d, mimeFilter)) return false;
    // When browsing, `docs` is already scoped to the current folder by
    // the fetch; only the search mode needs the client-side name match
    // (it fetches cross-folder results).
    if (!isSearchActive) return true;
    return d.name.toLowerCase().includes(search.toLowerCase());
  });

  const toggleSelection = useCallback(
    (doc: VaultDocumentDto) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(doc.documentId)) {
          next.delete(doc.documentId);
          return next;
        }
        if (!multiSelect) {
          next.clear();
          next.set(doc.documentId, doc);
          return next;
        }
        if (selectionLimit !== undefined && next.size >= selectionLimit) {
          toast({
            title: 'Selection limit reached',
            description: selectionLimitMessage ?? defaultSelectionLimitMessage(selectionLimit),
            variant: 'destructive',
          });
          return next;
        }
        next.set(doc.documentId, doc);
        return next;
      });
    },
    [multiSelect, selectionLimit, selectionLimitMessage],
  );

  /**
   * Upload one or more local files into the Vault and select them.
   *
   * Sequential rather than parallel: each upload is a presign + full-body PUT,
   * and the optimistic `setDocs` prepend below assumes a stable order. Per-file
   * failures are collected into one summary toast so picking ten files can't
   * stack ten toasts.
   */
  const handleUploads = useCallback(
    async (files: File[]) => {
      if (!holderId || files.length === 0) return;
      const rejected: string[] = [];
      const accepted: File[] = [];
      for (const file of files) {
        if (file.size > maxUploadSize) {
          rejected.push(`${file.name} (over ${formatBytes(maxUploadSize)})`);
          continue;
        }
        const err = validateUpload?.(file);
        if (err) {
          rejected.push(`${file.name} — ${err}`);
          continue;
        }
        accepted.push(file);
      }
      if (rejected.length > 0) {
        const MAX_NAMES = 3;
        const shown = rejected.slice(0, MAX_NAMES).join(', ');
        const extra = rejected.length - MAX_NAMES;
        toast({
          title: rejected.length === files.length ? 'Upload rejected' : 'Some files skipped',
          description: extra > 0 ? `${shown} +${extra} more` : shown,
          variant: 'destructive',
        });
      }
      if (accepted.length === 0) return;

      setIsUploading(true);
      const failed: string[] = [];
      try {
        for (const file of accepted) {
          try {
            // Upload into the folder the user is currently browsing so the
            // optimistic prepend below matches what a refetch would return.
            const { document } = await uploadLocalFileToVault(file, holderId, {
              folderId: currentFolderId,
            });
            setDocs((prev) => [document, ...prev]);
            setSelected((prev) => {
              const next = new Map(prev);
              if (!multiSelect) next.clear();
              else if (selectionLimit !== undefined && next.size >= selectionLimit) {
                // Uploaded, but the selection is full — leave it in the list for
                // the user to pick manually rather than silently dropping it.
                return next;
              }
              next.set(document.documentId, document);
              return next;
            });
          } catch (err) {
            failed.push(file.name);
            captureError(err instanceof Error ? err : new Error(String(err)), {
              source: 'vaultFilePicker.upload',
              fileName: file.name,
            });
          }
        }
      } finally {
        setIsUploading(false);
      }
      if (failed.length > 0) {
        const MAX_NAMES = 3;
        const shown = failed.slice(0, MAX_NAMES).join(', ');
        const extra = failed.length - MAX_NAMES;
        toast({
          title: 'Upload failed',
          description: extra > 0 ? `${shown} +${extra} more` : shown,
          variant: 'destructive',
        });
      }
    },
    [holderId, maxUploadSize, validateUpload, multiSelect, selectionLimit, currentFolderId],
  );

  const handleConfirm = useCallback(async () => {
    if (selected.size === 0) return;
    setIsConfirming(true);
    try {
      const results = await Promise.all(Array.from(selected.values()).map(toPickerResult));
      if (multiSelect && onFilesSelected) {
        onFilesSelected(results);
      } else if (onFileSelected) {
        onFileSelected(results[0]);
      }
    } catch (err) {
      toast({
        title: 'Selection failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  }, [selected, multiSelect, onFilesSelected, onFileSelected]);

  /** Confirm with a single doc directly (double-click flow). Bypasses
   *  `selected` because the click + double-click sequence races the
   *  toggleSelection setState and the doc the user actually double-
   *  clicked is unambiguous from the event. In multi-select mode this
   *  intentionally returns only the double-clicked doc — the gesture
   *  reads as "open this one now," not "add to existing selection."
   *
   *  It still honors `selectionLimit === 0`: bypassing the selection set must
   *  not become a way past the consumer's cap, or the consumer accepts a file
   *  it has no room for and fails downstream. */
  const confirmDoc = useCallback(
    async (doc: VaultDocumentDto) => {
      if (selectionLimit !== undefined && selectionLimit <= 0) {
        toast({
          title: 'Selection limit reached',
          description: selectionLimitMessage ?? defaultSelectionLimitMessage(selectionLimit),
          variant: 'destructive',
        });
        return;
      }
      setIsConfirming(true);
      try {
        const result = await toPickerResult(doc);
        if (multiSelect && onFilesSelected) {
          onFilesSelected([result]);
        } else if (onFileSelected) {
          onFileSelected(result);
        }
      } catch (err) {
        toast({
          title: 'Selection failed',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      } finally {
        setIsConfirming(false);
      }
    },
    [multiSelect, onFilesSelected, onFileSelected, selectionLimit, selectionLimitMessage],
  );

  const computedConfirmLabel =
    confirmLabel ??
    (multiSelect
      ? selected.size > 0
        ? `Add ${selected.size} ${selected.size === 1 ? 'file' : 'files'}`
        : 'Add files'
      : 'Select');

  return (
    <div className={cn('flex flex-col h-full min-h-0', className)}>
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="pl-8"
          />
        </div>
        {allowUpload && (
          <Button asChild variant="outline" disabled={isUploading || isConfirming}>
            <label className="cursor-pointer">
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload
              <input
                type="file"
                accept={uploadAccept}
                // Opt-in per consumer. Consumers that never asked for it keep
                // their existing one-file-at-a-time behavior untouched.
                multiple={multiUpload}
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length > 0) void handleUploads(files);
                  e.target.value = '';
                }}
              />
            </label>
          </Button>
        )}
      </div>

      {/* Breadcrumb — folder navigation only; hidden during search,
          which is cross-folder. Each crumb slices the stack back to
          that depth. The last crumb (current folder) isn't clickable. */}
      {!isSearchActive && folderStack.length > 1 && (
        <div className="flex items-center gap-1 mb-2 text-sm text-muted-foreground overflow-x-auto">
          {folderStack.map((crumb, i) => {
            const isLast = i === folderStack.length - 1;
            return (
              <span key={`${crumb.id ?? 'root'}-${i}`} className="flex items-center gap-1 shrink-0">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                <button
                  type="button"
                  onClick={() => setFolderStack((s) => s.slice(0, i + 1))}
                  disabled={isLast}
                  className={cn(
                    'truncate max-w-[12rem] hover:text-foreground disabled:cursor-default',
                    isLast && 'text-foreground font-medium',
                  )}
                >
                  {crumb.name}
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex-1 min-h-0 border rounded-lg overflow-auto">
        {loadError ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 text-sm text-muted-foreground text-center px-6">
            <span>Couldn&apos;t load files. {loadError}</span>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading files...
          </div>
        ) : folders.length === 0 && filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground text-center px-6">
            {isSearchActive
              ? 'No files match your search or filter.'
              : folderStack.length > 1
                ? 'This folder is empty.'
                : 'No files yet. Upload one to get started.'}
          </div>
        ) : (
          <div className="divide-y">
            {/* Folder rows first (browse mode only); clicking one drills
                in by pushing onto the breadcrumb stack. */}
            {!isSearchActive &&
              folders.map((folder) => (
                <button
                  key={folder.folderId}
                  type="button"
                  onClick={() =>
                    setFolderStack((s) => [...s, { id: folder.folderId, name: folder.name }])
                  }
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="h-9 w-9 shrink-0 flex items-center justify-center rounded bg-muted/40">
                    <Folder className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{folder.name}</div>
                    <div className="text-xs text-muted-foreground">Folder</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            {filtered.map((doc) => {
              const Icon = iconFor(doc.mimeType);
              const isSelected = selected.has(doc.documentId);
              const thumbnailUrl = thumbnails.get(doc.documentId);
              return (
                <button
                  key={doc.documentId}
                  type="button"
                  onClick={() => toggleSelection(doc)}
                  /* Double-click = "open this one now". Bypasses the
                     selection state via confirmDoc so the doc the user
                     actually double-clicked is the one delivered to
                     the parent. */
                  onDoubleClick={() => void confirmDoc(doc)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors',
                    isSelected && 'bg-primary/10 hover:bg-primary/15',
                  )}
                >
                  {/* Unified 36×36 thumbnail/icon slot — image rows
                      show the real bytes (presigned URL), everything
                      else gets the mime-family icon centered. Fixed
                      width keeps rows aligned across mixed types. */}
                  <div className="h-9 w-9 shrink-0 flex items-center justify-center rounded bg-muted/40 overflow-hidden">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{doc.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {doc.mimeType || 'unknown'} · {formatBytes(doc.sizeBytes)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3">
        {selected.size > 0 && (
          <div className="mr-auto text-sm text-muted-foreground flex items-center gap-2">
            <span>{selected.size} selected</span>
            <button
              type="button"
              onClick={() => setSelected(new Map())}
              className="text-xs underline hover:text-foreground"
            >
              <X className="h-3 w-3 inline mr-0.5" /> clear
            </button>
          </div>
        )}
        <Button
          onClick={handleConfirm}
          disabled={selected.size === 0 || isConfirming}
          className="ml-auto"
        >
          {isConfirming ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            computedConfirmLabel
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog — the canonical entry point. Wraps the panel in a modal.
// ---------------------------------------------------------------------------

export function VaultFilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  title = 'Select File',
  description,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  multiUpload = false,
  maxUploadSize = DEFAULT_MAX_UPLOAD,
  validateUpload,
  selectionLimit,
  selectionLimitMessage,
}: VaultFilePickerDialogProps) {
  const handleFile = useCallback(
    (file: VaultPickerResult) => {
      onFileSelected?.(file);
      onOpenChange(false);
    },
    [onFileSelected, onOpenChange],
  );
  const handleFiles = useCallback(
    (files: VaultPickerResult[]) => {
      onFilesSelected?.(files);
      onOpenChange(false);
    },
    [onFilesSelected, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 min-h-0">
          <VaultFilePickerPanel
            isActive={open}
            onFileSelected={handleFile}
            onFilesSelected={handleFiles}
            multiSelect={multiSelect}
            confirmLabel={confirmLabel}
            mimeFilter={mimeFilter}
            allowUpload={allowUpload}
            uploadAccept={uploadAccept}
            multiUpload={multiUpload}
            maxUploadSize={maxUploadSize}
            validateUpload={validateUpload}
            selectionLimit={selectionLimit}
            selectionLimitMessage={selectionLimitMessage}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
export default VaultFilePickerDialog;
